/**
 * The infinite grid view provides support for a grid view of items with infinite
 * scroll capabilities. The number of items per column, and the size of each item,
 * is determined dynamically by this view. This view windows the items so that
 * there is only a fixed amount of items in the DOM regardless of how many have been
 * scrolled. The grid builds grid items from the itemProps and the child component;
 * these items are rendered into the content scroller.
 *
 * Example:
 *  import InfiniteGrid from '../../components/Grid/InfiniteGrid';
 *  // Use the resize grid to get the required props
 *  // of divHeight and divWidth for free. Pass true as second
 *  // param to indicate that you're using the window to scroll.
 *  const ResizeGrid = ResizeMeasurer(InfiniteGrid, true);
 *  React.createClass({
 *   renderGridItems() {
 *     const { stationIds } = this.props;
 *     return stationIds.map((stationId) => {
 *       return {
 *         stationId,
 *         key: stationId
 *       };
 *     });
 *  },
 *  getSpinner() {
 *    return (
 *       <div style={{
 *         width: '50px',
 *         height: '50px',
 *         margin: 'auto',
 *         background: 'blue'
 *       }} className='spinner'>spinner</div>
 *     )
 *   },
 *  render() {
 *     const items = this.renderGridItems();
 *     return (
 *       <ResizeGrid
 *         childComponent={MyStationsGridItem}
 *         itemProps={items}
 *         useWindowToScroll={true}
 *         // The paginating api should tell you the total if
 *         // it is finite
 *         total={this.props.totalStations}
 *         // This should be true while your lazy loading
 *         // api is fetching and false once it has returned
 *         // successfully or with a failure
 *         isInfiniteLoading={this.props.isFetching}
 *         // Make the api call
 *         onInfiniteLoad={this.fetchMoreStations}
 *         // Return markup
 *         spinnerDelegate={this.getSpinner}
 *         />
 *     );
 *   },
 *  });
 */

import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';

import * as Constants from '../../constants';
import {
  addWindowEventListener,
  clearTimeout,
  removeWindowEventListener,
  setTimeout,
} from '../../tools/Globals';

import './InfiniteGrid.scss';

const { array, number, any, object, bool, func } = PropTypes;

function getItemsPerRowBasedOnWidth(width) {
  const gridConsts = Constants.Grid.ResponsiveGridConstants;
  let numColumns = gridConsts.MEDIUM_ITEMS_PER_ROW_COUNT;
  if (width < gridConsts.MIN_BREAKPOINT_PX) {
    numColumns = gridConsts.MIN_ITEMS_PER_ROW_COUNT;
  } else if (width < gridConsts.MEDIUM_BREAKPOINT_PX) {
    numColumns = gridConsts.SMALL_ITEMS_PER_ROW_COUNT;
  } else if (width >= gridConsts.XLARGE_BREAKPOINT_PX) {
    numColumns = gridConsts.XLARGE_ITEMS_PER_ROW_COUNT;
  }
  return numColumns;
}

/*
 * Given some grid measurements and parameters, returns an object containing
 * windowing parameters. The windowing parameters are calculated from the input
 * data and tuningParams.
 * scrollTop - The number of pixels scrolled off the
 * top of the grid's container
 * rowHeight - The height of each grid row in pixels
 * contentHeight - The total height of the grid
 * returns {Object} Derived grid windowing parameters. Includes firstIndex
 * (the index of the first grid item in the visible + buffer zone), and
 * lastIndex (the index of the last grid item in the visible + buffer zone).
 * Includes lowerScrollBound and upperScrollBound, which control when the
 * grid's onInfiniteLoad should be called (it will be called when
 * the grid container's scrollTop leaves this range).
 */
export function calculateWindowingParameters(
  { scrollTop, rowHeight, contentWidth, contentHeight, total = Infinity, fetchedCount },
  tuningParams = Constants.Grid.GridConstants,
) {
  const itemsPerRow = getItemsPerRowBasedOnWidth(contentWidth);
  const firstRow = Math.floor(scrollTop / rowHeight);
  const lastRow = firstRow + Math.ceil(contentHeight / rowHeight) + 1;
  const firstVisibleIndex = firstRow * itemsPerRow;
  const firstIndex = Math.max(0, firstVisibleIndex - itemsPerRow * tuningParams.NUM_BUFFER_ROWS);
  let lastIndex = lastRow * itemsPerRow + itemsPerRow * tuningParams.NUM_BUFFER_ROWS - 1;
  const lowerScrollBound = scrollTop - rowHeight;
  let upperScrollBound = scrollTop + rowHeight;
  if (lastIndex >= total) {
    upperScrollBound = Infinity;
    // Since total is not zero-indexed, we need to account for this
    lastIndex = total;
  }
  const pageSize = lastIndex - firstIndex + itemsPerRow * tuningParams.NUM_BUFFER_ROWS;
  return {
    firstIndex,
    firstVisibleIndex,
    lastIndex,
    lastRow,
    itemsPerRow,
    lowerScrollBound,
    upperScrollBound,
    pageSize,
    rowHeight,
  };
}

class InfiniteGrid extends React.PureComponent {
  static propTypes = {
    itemProps: array,
    divWidth: number.isRequired,
    divHeight: number.isRequired,
    childComponent: any.isRequired,
    tuningParams: object,
    useWindowToScroll: bool,
    total: number,
    reFetchData: bool,
    onInfiniteLoad: func,
    isInfiniteLoading: bool,
    spinnerDelegate: func,
    // Static item props are props known by the grid to be given as context
    // to all items
    staticItemProps: object,
    divOffsetTop: number,
  };

  static defaultProps = {
    tuningParams: Constants.Grid.GridConstants,
    useWindowToScroll: false,
    onInfiniteLoad: noop,
    isInfiniteLoading: false,
    spinnerDelegate: () => {
      return null;
    },
    staticItemProps: {},
  };

  constructor(props) {
    super(props);

    this.lastScrollTime = null;
    this.scrollTimeout = null;
    this.hasFinishedScrollingTimeout = null;

    const windowingParameters = calculateWindowingParameters({
      scrollTop: 0,
      rowHeight: Math.max(1, props.divHeight), // use divHeight to avoid initial display-cut-off issue
      contentWidth: props.divWidth,
      contentHeight: props.divHeight,
      fetchedCount: 0,
      total: props.total,
    });

    this.state = {
      scrollTop: 0,
      colWidth: 0,
      // This isScrolling parameter is used for toggling pointer events
      // when we are scrolling we disable pointer events to make scrolling more performant
      isScrolling: false,
      windowingParameters,
    };
  }

  componentWillMount() {
    if (!this.props.itemProps || this.props.itemProps.length === 0) {
      this.props.onInfiniteLoad();
    }
  }

  componentDidMount() {
    if (this.props.useWindowToScroll) {
      addWindowEventListener('scroll', this.onScroll);
    }
  }

  componentWillReceiveProps(nextProps) {
    const scrollTop =
      this.props.divOffsetTop === nextProps.divOffsetTop
        ? this.state.scrollTop
        : Math.max(0, window.pageYOffset - nextProps.divOffsetTop);
    const windowingParameters = calculateWindowingParameters({
      scrollTop,
      rowHeight: this.getRowHeight(),
      contentWidth: nextProps.divWidth,
      contentHeight: nextProps.divHeight,
      fetchedCount: nextProps.itemProps.length,
      total: nextProps.total,
    });
    // Avoid duplicate requests by checking for isInfiniteLoading
    if (
      (windowingParameters.lastIndex > nextProps.total &&
        nextProps.itemProps.length < this.props.total &&
        !nextProps.isInfiniteLoading) ||
      nextProps.reFetchData
    ) {
      this.props.onInfiniteLoad({ windowingParameters });
    }
    const newState = {
      scrollTop,
      windowingParameters,
      // NOTE: there's a quirk in IE that it doesn't measure this
      // column width on initial render the same as other browsers
      // so we have to measure more than we might want optimally
      colWidth: this.getColWidth(),
    };
    if (!isEqual(newState, this.state)) {
      this.setState(newState);
    }
  }

  componentWillUnmount() {
    if (this.props.useWindowToScroll) {
      removeWindowEventListener('scroll', this.onScroll);
    }
    if (this.hasFinishedScrollingTimeout) clearTimeout(this.hasFinishedScrollingTimeout);
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  onScroll = () => {
    const now = Date.now();
    if (now < this.lastScrollTime + this.props.tuningParams.THROTTLE_RATE) {
      if (!this.scrollTimeout) {
        this.scrollTimeout = setTimeout(this.onScroll, this.props.tuningParams.THROTTLE_RATE);
      }
      return;
    }

    if (this.hasFinishedScrollingTimeout) clearTimeout(this.hasFinishedScrollingTimeout);

    this.hasFinishedScrollingTimeout = setTimeout(() => {
      this.setState({ isScrolling: false });
    }, Constants.Grid.GridConstants.TIME_TO_WAIT_FOR_SCROLL_TO_END_MS);

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    this.lastScrollTime = now;

    const scrollTop = this.props.useWindowToScroll
      ? Math.max(0, window.pageYOffset - this.props.divOffsetTop)
      : this.refs.grid.scrollTop;
    const { lowerScrollBound, upperScrollBound, lastIndex } = this.state.windowingParameters;
    const isScrolledOutOfBound = scrollTop < lowerScrollBound || scrollTop > upperScrollBound;
    const mightMissAFetch = lastIndex >= this.props.itemProps.length;

    if (isScrolledOutOfBound || mightMissAFetch) {
      const windowingInputs = {
        scrollTop,
        rowHeight: this.getRowHeight(),
        contentWidth: this.props.divWidth,
        contentHeight: this.props.divHeight,
        fetchedCount: this.props.itemProps.length,
        total: this.props.total,
      };
      const windowingParameters = calculateWindowingParameters(windowingInputs);
      this.props.onInfiniteLoad({ windowingParameters });

      this.setState({
        scrollTop,
        windowingParameters,
        isScrolling: true,
      });
    }
  };

  getRowHeight = () => {
    const firstItem = this.refs.firstItem;
    if (firstItem && firstItem.childElementCount > 0) {
      return firstItem.offsetHeight;
    }

    // return Math.max(1, divHeight) to avoid being zero denominator
    // use divHeight to avoid initial display-cut-off issue
    return Math.max(1, this.props.divHeight);
  };

  getColWidth = () => {
    if (this.refs.firstItem) {
      return this.refs.firstItem.offsetWidth;
    }

    return 0;
  };

  render() {
    if (!this.state.windowingParameters || !this.props.itemProps) {
      return null;
    }
    const { firstIndex, lastIndex, itemsPerRow, rowHeight } = this.state.windowingParameters;
    const views = [];
    const total = this.props.total
      ? this.props.total
      : Math.min(lastIndex, this.props.itemProps.length);
    for (let i = firstIndex; i <= lastIndex; i++) {
      const item = this.props.itemProps[i];
      if (item) {
        let style = {};
        if (this.state.colWidth) {
          const x = (i % itemsPerRow) * this.state.colWidth;
          const y = Math.floor(i / itemsPerRow) * rowHeight;
          const transformVal = `translate3d(${x}px,${y}px,0px)`;
          style = {
            position: 'absolute',
            transform: transformVal,
            WebkitTransform: transformVal,
          };
        }
        views.push(
          <div
            style={style}
            key={item.key}
            className='InfiniteGrid__contents__itemContainer'
            ref={i === firstIndex ? 'firstItem' : null}
          >
            {
              <this.props.childComponent
                item={item}
                {...{ ...this.props.staticItemProps, ...item }}
              />
            }
          </div>,
        );
      }
    }
    const spinner = this.props.isInfiniteLoading ? this.props.spinnerDelegate() : null;
    const gridStyles = {
      height: '100%',
      pointerEvents: this.state.isScrolling ? 'none' : 'auto',
    };
    if (this.props.useWindowToScroll) {
      gridStyles.overflow = 'hidden';
    }
    return (
      <div
        className='InfiniteGrid'
        onScroll={this.props.useWindowToScroll ? noop : this.onScroll}
        style={gridStyles}
        ref='grid'
      >
        <div
          className='InfiniteGrid__contents'
          style={{ height: Math.ceil(total / itemsPerRow) * rowHeight }}
        >
          {views}
          {spinner}
        </div>
      </div>
    );
  }
}

export default InfiniteGrid;
